import { observable, runInAction } from "mobx"

import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { compareStringAsInteger } from "@/lib/std.ts"

import type { ListModel } from "@/components/Chat/LoadMoreButton.tsx"
import type { BatchInfo, LLMLogPage, MessagesSorting } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("LLMLogPagesModel")

export interface LLMLogPagesModel extends ListModel {
  pages: IObservableValue<LLMLogPage[]>
  hasNext: IObservableValue<boolean>
  hasPrev: IObservableValue<boolean>
  isLoadingNext: IObservableValue<boolean>
  isLoadingPrev: IObservableValue<boolean>
  isLoadingInitially: IObservableValue<boolean>
  isReady: IObservableValue<boolean>

  loadNext(): void

  loadPrev(): void

  terminate(): void
}

export function createLLMLogPagesModel(
  taskId: string,
  queries: Queries,
  messageId: string | null = null,
  batchSize: number = 20,
): LLMLogPagesModelImpl {
  return new LLMLogPagesModelImpl(taskId, queries, messageId, batchSize)
}

class LLMLogPagesModelImpl extends Model implements LLMLogPagesModel {
  private readonly pageComparator: (a: LLMLogPage, b: LLMLogPage) => number = (a, b) => {
    // Sort by creation time, newest first
    return compareStringAsInteger(a.id, b.id)
  }

  private readonly rawPages = new Map<string, LLMLogPage>()
  private readonly updateQueue: LoadType[] = []
  private isProcessing: boolean = false

  pages = observable.box<LLMLogPage[]>([])
  hasNext = observable.box(false)
  hasPrev = observable.box(true)
  isLoadingNext = observable.box(false)
  isLoadingPrev = observable.box(false)
  isLoadingInitially = observable.box(true)
  isReady = observable.box(false)

  constructor(private readonly taskId: string, private readonly queries: Queries, private readonly messageId: string | null = null, private readonly batchSize: number = 20) {
    super("LLMLogPagesModelImpl")

    void this.startQueueProcessing()
    void this.listenForUpdates()
  }

  readonly loadNext = () => {
    this.scheduleUpdate("load-next")
  }

  readonly loadPrev = () => {
    this.scheduleUpdate("load-prev")
  }

  private listenForUpdates() {
    const updatesChannel = this.queries.llmLogPageUpdates(this.taskId, null)
    this.scheduleUpdate("update-recent")

    this.lifetime.add(updatesChannel.subscribe(() => {
      if (!this.hasNext.get()) {
        this.scheduleUpdate("update-recent")
      }
    }), "LLMLogPagesModelImpl:updatesChannel")
  }

  terminate() {
    super.terminate()
    this.updateQueue.length = 0
  }

  private async startQueueProcessing() {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true

    while (this.updateQueue.length > 0) {
      try {
        const request = this.updateQueue.shift()!
        log.debug("Process event", request)
        if (request === "load-next" && !this.isLoadingNext.get() && this.hasNext.get()) {
          await this.load("load-next")
        } else if (request === "load-prev" && !this.isLoadingPrev.get() && this.hasPrev.get()) {
          await this.load("load-prev")
        } else {
          await this.load(request)
        }
      } catch (ex) {
        log.error("Error during queue processing:", ex)
      }
    }

    this.isProcessing = false
  }

  private scheduleUpdate(update: LoadType) {
    for (const existingUpdate of this.updateQueue) {
      if (existingUpdate === update) {
        return
      }
    }

    this.updateQueue.push(update)

    if (!this.isProcessing) {
      void this.startQueueProcessing()
    }
  }

  private getLatestPageId(): string | null {
    if (this.rawPages.size == 0) {
      return null
    }

    return [...this.rawPages.values()].reduce((max, current) =>
      compareStringAsInteger(current.id, max.id) > 0 ? current : max
    ).id
  }

  private getOldestPageId(): string | null {
    if (this.rawPages.size == 0) {
      return null
    }

    return [...this.rawPages.values()].reduce((min, current) =>
      compareStringAsInteger(current.id, min.id) < 0 ? current : min
    ).id
  }

  private consumeBatch(batch: LLMLogPage[]) {
    for (const item of batch) {
      this.rawPages.set(item.id, item)
    }

    this.updatePagesList()
  }

  private resetState() {
    runInAction(() => {
      this.rawPages.clear()
      this.updateQueue.length = 0
      this.hasNext.set(false)
      this.hasPrev.set(true)
    })
  }

  private async getBatch(offset: string | null, sorting: MessagesSorting, type: LoadType): Promise<LLMLogPage[]> {
    const batchInfo: BatchInfo = { offset, batchSize: this.batchSize }
    const batch = (await this.queries.llmLogPages(this.taskId, batchInfo, sorting, this.messageId)).data
    if (batch.length < this.batchSize) {
      if (sorting === "FromNewestToOldest") {
        runInAction(() => this.hasPrev.set(false))
      } else if (sorting === "FromOldestToNewest") {
        runInAction(() => this.hasNext.set(false))
      }
    } else if (type === "update-recent") {
      let newPagesCount = 0
      for (const page of batch) {
        if (!this.rawPages.has(page.id)) {
          newPagesCount++
        }
      }
      if (newPagesCount >= this.batchSize) {
        // Too many updates, try to reload
        this.resetState()
      }
    }
    return batch
  }

  private async load(type: LoadType) {
    let sorting: MessagesSorting | null = null
    let offset: string | null = null

    runInAction(() => {
      this.isLoadingNext.set(type === "load-next")
      this.isLoadingPrev.set(type === "load-prev")
    })

    try {
      if (type === "load-prev") {
        sorting = "FromNewestToOldest"
        offset = this.getOldestPageId()
      } else if (type === "load-next") {
        sorting = "FromOldestToNewest"
        offset = this.getLatestPageId()
      } else if (type === "update-recent") {
        sorting = "FromNewestToOldest"
        offset = null
      } else {
        return
      }

      this.consumeBatch(await this.getBatch(offset, sorting, type))
    } finally {
      runInAction(() => {
        this.isLoadingPrev.set(false)
        this.isLoadingNext.set(false)
        this.isLoadingInitially.set(false)
        this.isReady.set(true)
      })
    }
  }

  private updatePagesList() {
    const pages: LLMLogPage[] = []
    for (const page of this.rawPages.values()) {
      pages.push(page)
    }

    pages.sort(this.pageComparator)
    log.debug(`Total pages count: ${pages.length}`)

    runInAction(() => this.pages.set(pages))
  }
}

type LoadType = "update-recent" | "load-next" | "load-prev"

